import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import { selectIdentity } from "../../../selectors/userSelectors";
import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import NumberedPagination from "../../../shared/ui/Pagination/NumberedPagination";
import { messageFromError, parseDrfFieldErrors } from "../../../shared/api/drfErrors";
import {
  fetchCompany,
  fetchMembers,
  fetchMyCompany,
} from "../../companyWorkspace/api/companyWorkspace";
import {
  isAdminMembership,
  isActiveMembership,
} from "../../companyWorkspace/lib/companyLabels";
import {
  createScopeGate,
  sameActorId,
  sameCompanyId,
} from "../../companyWorkspace/lib/scopeSession";
import {
  FOCUS_REVALIDATION_UNRESOLVED_MESSAGE,
  openBackgroundRevalidation,
  settleRevalidationWrites,
  useCompanyFocusRevalidation,
} from "../../companyWorkspace/lib/companyFocusRevalidation";
import CompanyPageHeader from "../../companyWorkspace/ui/CompanyPageHeader";

import {
  createCompanyClient,
  fetchCompanyClients,
} from "../api/companyClients";
import {
  clientDisplayName,
  isCorporateClientsAvailable,
  statusLabel,
  typeLabel,
  userDisplayName,
} from "../lib/clientLabels";
import {
  buildCorporateListParams,
  readCorporateListQuery,
  writeCorporateListSearch,
} from "../lib/listQuery";
import CompanyClientCreateModal, {
  CORE_FIELD_KEYS,
} from "./CompanyClientCreateModal";

function unwrapPage(data) {
  if (Array.isArray(data)) {
    return { results: data, count: data.length, next: null, previous: null };
  }
  return {
    results: Array.isArray(data?.results) ? data.results : [],
    count: typeof data?.count === "number" ? data.count : 0,
    next: data?.next ?? null,
    previous: data?.previous ?? null,
  };
}

function unwrapMembers(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
}

function CompanyClientsListContent() {
  const { companyId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const identity = useSelector(selectIdentity);
  const actorId = identity?.userId ?? null;
  const scopeGate = useRef(createScopeGate()).current;
  const mountedRef = useRef(true);
  const busyOpRef = useRef(null);

  const queryState = readCorporateListQuery(location.search);
  const queryKey = [
    queryState.query,
    queryState.type || "",
    queryState.status || "",
    String(queryState.page),
  ].join("|");
  const [draftQuery, setDraftQuery] = useState(queryState.query);
  const [draftType, setDraftType] = useState(queryState.type);
  const [draftStatus, setDraftStatus] = useState(queryState.status);

  const [company, setCompany] = useState(null);
  const [membership, setMembership] = useState(null);
  const [authorityActorId, setAuthorityActorId] = useState(null);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [nextUrl, setNextUrl] = useState(null);
  const [prevUrl, setPrevUrl] = useState(null);
  const [state, setState] = useState("loading");
  const stateRef = useRef(state);
  const [writesSuspended, setWritesSuspended] = useState(false);
  const [actionError, setActionError] = useState(null);
  const writesSuspendedRef = useRef(false);
  const revalidationTokenRef = useRef(0);
  stateRef.current = state;
  writesSuspendedRef.current = writesSuspended;
  const [error, setError] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [createFieldErrors, setCreateFieldErrors] = useState({});
  const [createMembers, setCreateMembers] = useState([]);

  const companyMatches =
    company != null && sameCompanyId(company.id, companyId);
  const actorMatches =
    authorityActorId != null && sameActorId(authorityActorId, actorId);
  const authorityMatches = companyMatches && actorMatches;
  const available =
    authorityMatches && isCorporateClientsAvailable(company);
  const canCreate =
    available && isActiveMembership(company, membership);
  const canManageQueue =
    available && isAdminMembership(company, membership);
  const canSelectResponsibility = canManageQueue;

  const clearCreateUi = useCallback(() => {
    setCreateOpen(false);
    setCreateBusy(false);
    setCreateError(null);
    setCreateFieldErrors({});
    setCreateMembers([]);
  }, []);

  const load = useCallback(async ({ background = false } = {}) => {
    if (!companyId) {
      setState("not-found");
      return;
    }
    const keepVisible =
      background &&
      (stateRef.current === "ready" || stateRef.current === "unavailable");
    const op = scopeGate.beginRead({ companyId, actorId });
    const tokenAtStart = revalidationTokenRef.current;
    let backgroundToken = null;
    let revalidationConfirmed = false;
    if (keepVisible) {
      backgroundToken = openBackgroundRevalidation(
        revalidationTokenRef,
        writesSuspendedRef,
        setWritesSuspended,
      );
    } else {
      setState("loading");
      setError(null);
    }
    try {
      const [companyData, mine] = await Promise.all([
        fetchCompany(companyId),
        fetchMyCompany(),
      ]);
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      const mineMatches =
        mine?.company?.id != null && sameCompanyId(mine.company.id, companyId)
          ? mine.membership
          : null;
      setCompany(companyData);
      setMembership(mineMatches);
      setAuthorityActorId(actorId);

      if (!isActiveMembership(companyData, mineMatches)) {
        setRows([]);
        clearCreateUi();
        setState("denied");
        return;
      }
      if (!isCorporateClientsAvailable(companyData)) {
        setRows([]);
        clearCreateUi();
        setState("unavailable");
        return;
      }

      const page = await fetchCompanyClients(
        companyId,
        buildCorporateListParams({
          query: queryState.query,
          type: queryState.type,
          status: queryState.status,
          page: queryState.page,
        }),
      );
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      const unwrapped = unwrapPage(page);
      setRows(unwrapped.results);
      setCount(unwrapped.count);
      setNextUrl(unwrapped.next);
      setPrevUrl(unwrapped.previous);
      revalidationConfirmed = true;
      setActionError((current) =>
        current === FOCUS_REVALIDATION_UNRESOLVED_MESSAGE ? null : current,
      );
      setState("ready");
    } catch (err) {
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      const status = err?.response?.status;
      if (keepVisible && status !== 403 && status !== 404) {
        setActionError(FOCUS_REVALIDATION_UNRESOLVED_MESSAGE);
        return;
      }
      setRows([]);
      clearCreateUi();
      if (status === 404) {
        setState("not-found");
      } else if (status === 403) {
        setState("denied");
      } else {
        setError(messageFromError(err));
        setState("error");
      }
    } finally {
      settleRevalidationWrites({
        keepVisible: keepVisible,
        confirmed: revalidationConfirmed,
        token: backgroundToken,
        tokenAtStart,
        tokenRef: revalidationTokenRef,
        writesSuspendedRef,
        setWritesSuspended,
      });
    }
  }, [
    actorId,
    clearCreateUi,
    companyId,
    queryKey,
    scopeGate,
  ]);

  useEffect(() => {
    mountedRef.current = true;
    scopeGate.bindIdentity({ companyId, actorId });
    busyOpRef.current = null;
    clearCreateUi();
    setCompany(null);
    setMembership(null);
    setAuthorityActorId(null);
    setRows([]);
    load();
    return () => {
      mountedRef.current = false;
      scopeGate.invalidate();
      busyOpRef.current = null;
    };
  }, [companyId, actorId, queryKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setDraftQuery(queryState.query);
    setDraftType(queryState.type);
    setDraftStatus(queryState.status);
  }, [queryKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useCompanyFocusRevalidation({
    enabled: state === "ready" || state === "unavailable",
    busy: createBusy,
    onRevalidate: () => load({ background: true }),
  });

  useEffect(() => {
    if (!createOpen || !canSelectResponsibility || !companyId) {
      if (!canSelectResponsibility) {
        setCreateMembers([]);
      }
      return undefined;
    }
    let cancelled = false;
    const op = scopeGate.beginRead({ companyId, actorId });
    (async () => {
      try {
        const roster = await fetchMembers(companyId, "active");
        if (
          cancelled ||
          !scopeGate.readStillValid(op, { mounted: mountedRef.current })
        ) {
          return;
        }
        setCreateMembers(unwrapMembers(roster));
      } catch {
        if (
          cancelled ||
          !scopeGate.readStillValid(op, { mounted: mountedRef.current })
        ) {
          return;
        }
        setCreateMembers([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    createOpen,
    canSelectResponsibility,
    companyId,
    actorId,
    scopeGate,
  ]);

  const applyFilters = (event) => {
    event.preventDefault();
    navigate({
      pathname: `/companies/${companyId}/clients`,
      search: writeCorporateListSearch({
        query: draftQuery,
        type: draftType,
        status: draftStatus,
        page: 1,
      }),
    });
  };

  const selectSingle = (current, value, setter) => {
    setter(current === value ? "" : value);
  };

  const totalPages = Math.max(1, Math.ceil(count / 20) || 1);

  const handleCreate = async (body) => {
    if (!canCreate || busyOpRef.current || writesSuspendedRef.current) return;
    const op = scopeGate.beginMutation({ companyId, actorId });
    busyOpRef.current = op;
    setCreateBusy(true);
    setCreateError(null);
    setCreateFieldErrors({});
    try {
      const created = await createCompanyClient(companyId, body);
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      setCreateOpen(false);
      setCreateMembers([]);
      setCreateFieldErrors({});
      navigate(`/companies/${companyId}/clients/${created.id}`);
    } catch (err) {
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      const status = err?.response?.status;
      if (status === 403 || status === 404) {
        clearCreateUi();
        setRows([]);
        setState(status === 404 ? "not-found" : "denied");
        return;
      }
      if (status === 400) {
        const parsed = parseDrfFieldErrors(err, CORE_FIELD_KEYS);
        const { _form, ...fields } = parsed;
        setCreateFieldErrors(fields);
        setCreateError(_form || null);
        return;
      }
      setCreateError(messageFromError(err));
    } finally {
      if (busyOpRef.current === op) {
        busyOpRef.current = null;
        setCreateBusy(false);
      }
    }
  };

  return (
    <section
      className="clients-page feature-company-workspace feature-company-clients"
      id="Csection"
      tabIndex="-1"
    >
      <div className="container">
        <CompanyPageHeader
          title="Клієнти компанії"
          subtitle="Корпоративний CRM"
          company={authorityMatches ? company : null}
          membership={authorityMatches ? membership : null}
          companyId={companyId}
          activeNav="clients"
          actions={
            (canManageQueue || canCreate) && state === "ready" ? (
              <>
                {canManageQueue ? (
                  <Link
                    to={`/companies/${companyId}/clients/import`}
                    className="action-btn"
                  >
                    Імпорт
                  </Link>
                ) : null}
                {canManageQueue ? (
                  <Link
                    to={`/companies/${companyId}/clients/unassigned`}
                    className="action-btn"
                  >
                    Черга без відповідального
                  </Link>
                ) : null}
                {canCreate ? (
                  <button
                    type="button"
                    className="btn-more"
                    disabled={writesSuspended}
                    onClick={() => { if (!writesSuspended) setCreateOpen(true); }}
                  >
                    Новий клієнт
                  </button>
                ) : null}
              </>
            ) : null
          }
        />

        {actionError && state === "ready" ? (
          <p className="feature-company-workspace__error" role="alert">
            {actionError}
          </p>
        ) : null}

        {state === "loading" ? (
          <div className="feature-company-workspace__state">
            <Spinner />
          </div>
        ) : null}

        {state === "unavailable" ? (
          <EmptyState
            title="Корпоративні клієнти недоступні"
            description="Ця функція вимкнена для компанії. Записи не завантажуються."
          />
        ) : null}

        {state === "denied" ? (
          <EmptyState
            title="Немає доступу"
            description="Активне членство в цій компанії потрібне для перегляду клієнтів."
          />
        ) : null}

        {state === "not-found" ? (
          <EmptyState
            title="Не знайдено"
            description="Компанію або ресурс не знайдено."
          />
        ) : null}

        {state === "error" ? (
          <EmptyState
            title="Помилка завантаження"
            description={error || "Спробуйте оновити сторінку."}
            primaryAction={
              <button type="button" className="action-btn" onClick={load}>
                Повторити
              </button>
            }
          />
        ) : null}

        {state === "ready" && available ? (
          <div className="clients-workspace">
            <form
              className="clients-toolbar"
              onSubmit={applyFilters}
              aria-label="Фільтри клієнтів компанії"
            >
              <div className="clients-toolbar__row">
                <input
                  className="clients-search"
                  type="search"
                  value={draftQuery}
                  onChange={(e) => setDraftQuery(e.target.value)}
                  placeholder="Пошук"
                  aria-label="Пошук"
                />
                <button type="submit" className="action-btn">
                  Знайти
                </button>
                <button
                  type="button"
                  className="action-btn action-btn--ghost"
                  onClick={() => {
                    setDraftQuery("");
                    setDraftType("");
                    setDraftStatus("");
                    navigate(`/companies/${companyId}/clients`);
                  }}
                >
                  Скинути
                </button>
              </div>
              <div
                className="clients-filter-group feature-company-clients__filter-group"
                aria-label="Тип"
              >
                {["buyer", "seller", "both"].map((t) => (
                  <button
                    key={t}
                    type="button"
                    className={[
                      "filter-chip",
                      draftType === t ? "filter-chip--active" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    aria-pressed={draftType === t}
                    onClick={() => selectSingle(draftType, t, setDraftType)}
                  >
                    {typeLabel(t)}
                  </button>
                ))}
              </div>
              <div
                className="clients-filter-group feature-company-clients__filter-group"
                aria-label="Статус"
              >
                {["lead", "active", "inactive", "closed"].map((s) => (
                  <button
                    key={s}
                    type="button"
                    className={[
                      "filter-chip",
                      `filter-chip--${s}`,
                      draftStatus === s ? "filter-chip--active" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    aria-pressed={draftStatus === s}
                    onClick={() =>
                      selectSingle(draftStatus, s, setDraftStatus)
                    }
                  >
                    {statusLabel(s)}
                  </button>
                ))}
              </div>
            </form>

            {rows.length === 0 ? (
              <EmptyState
                title="Немає клієнтів"
                description="За поточними фільтрами корпоративних записів немає."
              />
            ) : (
              <div className="clients-table-wrap">
                <table className="clients-table">
                  <thead>
                    <tr>
                      <th>Клієнт</th>
                      <th>Тип</th>
                      <th>Статус</th>
                      <th>Відповідальний</th>
                      <th>Автор</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.id} className="client-row">
                        <th scope="row" className="client-row__name">
                          <Link
                            to={`/companies/${companyId}/clients/${row.id}`}
                            className="client-link"
                          >
                            <span className="client-link__text">
                              <span className="client-link__title">
                                {clientDisplayName(row)}
                              </span>
                              <span className="client-link__meta feature-company-clients__owner-note">
                                Компанія · не особистий запис
                              </span>
                            </span>
                          </Link>
                        </th>
                        <td data-c2-col="type">
                          <span
                            className={`type-pill type-pill--${row.type || "buyer"}`}
                          >
                            {typeLabel(row.type)}
                          </span>
                        </td>
                        <td data-c2-col="status">
                          <span
                            className={`status-pill status-pill--${row.status || "lead"}`}
                          >
                            {statusLabel(row.status)}
                          </span>
                        </td>
                        <td data-c2-col="responsible">
                          {userDisplayName(row.responsible_user)}
                        </td>
                        <td data-c2-col="author">
                          {userDisplayName(row.created_by)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <NumberedPagination
                  currentPage={queryState.page}
                  totalPages={totalPages}
                  hasPrevious={Boolean(prevUrl)}
                  hasNext={Boolean(nextUrl)}
                  ariaLabel="Сторінки клієнтів компанії"
                  onPageChange={(page) => {
                    navigate({
                      pathname: `/companies/${companyId}/clients`,
                      search: writeCorporateListSearch({
                        query: queryState.query,
                        type: queryState.type,
                        status: queryState.status,
                        page,
                      }),
                    });
                  }}
                />
              </div>
            )}
          </div>
        ) : null}

        {createOpen && canCreate && available && state === "ready" ? (
          <CompanyClientCreateModal
            busy={createBusy}
            error={createError}
            fieldErrors={createFieldErrors}
            canSelectResponsibility={canSelectResponsibility}
            members={createMembers}
            onClose={() => {
              if (!createBusy) {
                setCreateOpen(false);
                setCreateError(null);
                setCreateFieldErrors({});
                setCreateMembers([]);
              }
            }}
            onFieldEdit={(key) => {
              setCreateFieldErrors((prev) => {
                if (!prev[key]) return prev;
                const next = { ...prev };
                delete next[key];
                return next;
              });
            }}
            onSubmit={handleCreate}
          />
        ) : null}
      </div>
    </section>
  );
}

export default CompanyClientsListContent;
